// Admin (ArchviewVR staff only): every account, their studios and tours, free months, storage limits, cancelling, deleting.
import { PLAN, tourUrl } from '@archviewvr/config';
import { adminOverview, adminGiveFree, adminCancelTour, adminSetStorage, adminSetWhiteLabel, adminDeleteAccount, type AdminAccount, type AdminProject, type Studio } from '../data';
import { adminTabs } from './tutorial-edit';
import { html, raw, shell, formatBytes, formatLimit, toast, confirmDialog, $, $$ } from '../ui';

const day = (s: string | null) => (s ? new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const isFree = (p: AdminProject) => p.state === 'published' && !!p.free_until && new Date(p.free_until) > new Date();

function stateChip(p: AdminProject) {
  if (isFree(p)) return html`<span class="chip free"><span class="dot"></span>Free until ${day(p.free_until)}</span>`;
  const label = { trial: 'Trial', published: 'Paid', paused: 'Paused', archived: 'Archived' }[p.state];
  return html`<span class="chip ${p.state}"><span class="dot"></span>${label}</span>`;
}

function projectRow(p: AdminProject) {
  const live = p.state === 'published';
  const open = live
    ? html`<a href="${tourUrl(p.slug)}" target="_blank" rel="noopener">${tourUrl(p.slug).replace(/^https?:\/\//, '')}</a>`
    : html`<a href="/viewer/?draft=${p.id}" target="_blank" rel="noopener">Open preview</a>`;
  return html`<div class="ap-row" data-project="${p.id}" data-name="${p.name}">
    <div class="ap-name"><strong>${p.name}</strong>${stateChip(p)}</div>
    <div class="ap-link">${open}</div>
    <div class="ap-meta">${p.renders} renders · ${formatBytes(p.storage_bytes)} of ${formatLimit(p.storage_limit ?? PLAN.storageLimitBytes)}</div>
    <label class="ap-storage">Storage
      <select class="storage-size" aria-label="Storage limit for ${p.name}">
        ${PLAN.storageOptions.map((b) => html`<option value="${b}" ${b === (p.storage_limit ?? PLAN.storageLimitBytes) ? raw('selected') : ''} ${b < p.storage_bytes ? raw('disabled') : ''}>${formatLimit(b)}</option>`)}
      </select>
    </label>
    <label class="ap-wl" title="Hides “Powered by ArchviewVR” on this tour"><input type="checkbox" class="white-label" ${p.white_label ? raw('checked') : ''}> White label</label>
    <div class="ap-acts">
      <select class="free-months" aria-label="Free months">
        ${[1, 2, 3, 6, 12].map((m) => html`<option value="${m}">${m} month${m > 1 ? 's' : ''}</option>`)}
      </select>
      <button class="btn small" data-act="free">${isFree(p) ? 'Add free time' : 'Give free'}</button>
      ${live ? html`<button class="btn small ghost-bad" data-act="cancel">Cancel tour</button>` : ''}
    </div>
  </div>`;
}

function accountCard(a: AdminAccount) {
  const projects = a.studios.flatMap((s) => s.projects);
  const search = [a.email, a.name, ...a.studios.map((s) => s.name), ...projects.map((p) => `${p.name} ${p.slug}`)].join(' ').toLowerCase();
  return html`<article class="box acct" data-search="${search}" data-user="${a.id}" data-email="${a.email}">
    <header class="acct-head">
      <div>
        <div class="acct-name">${a.name || a.email}
          ${a.admin ? html`<span class="tag">Admin</span>` : ''}
          ${a.confirmed ? '' : html`<span class="tag warn">Email not confirmed</span>`}
        </div>
        <div class="muted acct-sub">${a.name ? html`<a href="mailto:${a.email}">${a.email}</a> · ` : ''}Joined ${day(a.created_at)} · Last signed in ${day(a.last_sign_in_at)}</div>
      </div>
      ${a.admin ? '' : html`<button class="btn small ghost-bad" data-act="delete">Delete account</button>`}
    </header>
    ${a.studios.length ? a.studios.map((s) => html`<section class="acct-studio">
        <div class="studio-line">${s.name} <em>${s.role}${s.members > 1 ? ` · ${s.members} people` : ''}</em></div>
        ${s.projects.length ? s.projects.map(projectRow) : html`<p class="muted small">No projects yet</p>`}
      </section>`) : html`<p class="muted small">Hasn’t set up a studio yet</p>`}
  </article>`;
}

export async function adminView(root: HTMLElement, studio: Studio): Promise<void> {
  const page = shell(root, { studioName: studio.name, active: 'admin' });
  page.innerHTML = '<div class="loading-page">Loading accounts</div>';
  let accounts: AdminAccount[];
  try { accounts = await adminOverview(); }
  catch (e) { page.innerHTML = html`<div class="empty"><h2>Couldn’t load admin</h2><p class="muted">${e instanceof Error ? e.message : ''}</p></div>`.html; return; }

  const projects = accounts.flatMap((a) => a.studios.flatMap((s) => s.projects));
  const uniq = new Map(projects.map((p) => [p.id, p])); // a project shows once per member, count it once
  const all = [...uniq.values()];
  const paid = all.filter((p) => p.state === 'published' && !isFree(p)).length;
  const stats: Array<[string, string]> = [
    ['Accounts', String(accounts.length)],
    ['Paid tours', String(paid)],
    ['Free tours', String(all.filter(isFree).length)],
    ['Trials', String(all.filter((p) => p.state === 'trial').length)],
    ['Monthly', `$${(paid * PLAN.priceAud).toLocaleString('en-AU')}`],
  ];

  page.innerHTML = html`${adminTabs('accounts')}<div class="page-head"><div><h1>Admin</h1><p class="sub">Everyone using ArchviewVR. Only you can see this.</p></div></div>
    <div class="stats">${stats.map(([k, v]) => html`<div class="box stat"><span>${k}</span><strong>${v}</strong></div>`)}</div>
    <input class="search" id="admin-search" type="search" placeholder="Search email, name, studio or project">
    <div class="accts" id="accts">${accounts.map(accountCard)}</div>
    <p class="muted small" id="no-match" hidden>No accounts match.</p>`.html;

  const search = $<HTMLInputElement>('#admin-search', page);
  search.oninput = () => {
    const q = search.value.trim().toLowerCase();
    let shown = 0;
    for (const el of $$('.acct', page)) { const on = !q || (el.dataset.search ?? '').includes(q); el.hidden = !on; if (on) shown++; }
    $('#no-match', page).hidden = shown > 0;
  };

  const reload = () => adminView(root, studio);

  page.addEventListener('change', async (e) => {
    const wl = (e.target as HTMLElement).closest<HTMLInputElement>('input.white-label');
    const wlRow = wl?.closest<HTMLElement>('.ap-row');
    if (wl && wlRow) {
      wl.disabled = true;
      try {
        await adminSetWhiteLabel(wlRow.dataset.project!, wl.checked);
        toast(wl.checked ? `${wlRow.dataset.name} is white label: no “Powered by ArchviewVR”` : `${wlRow.dataset.name} shows “Powered by ArchviewVR” again`);
      } catch (err) {
        wl.checked = !wl.checked;
        toast(err instanceof Error ? err.message : 'That didn’t work', 'error', 8000);
      }
      wl.disabled = false;
      return;
    }
    const sel = (e.target as HTMLElement).closest<HTMLSelectElement>('select.storage-size');
    const row = sel?.closest<HTMLElement>('.ap-row');
    if (!sel || !row) return;
    const bytes = Number(sel.value);
    sel.disabled = true;
    try {
      await adminSetStorage(row.dataset.project!, bytes);
      toast(`${row.dataset.name} now has ${formatLimit(bytes)} of storage`);
      await reload();
    } catch (err) {
      sel.disabled = false;
      toast(err instanceof Error ? err.message : 'That didn’t work', 'error', 8000);
      await reload();
    }
  });

  page.addEventListener('click', async (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-act]');
    if (!btn) return;
    const row = btn.closest<HTMLElement>('.ap-row');
    const card = btn.closest<HTMLElement>('.acct')!;
    const act = btn.dataset.act;
    try {
      if (act === 'free' && row) {
        const months = Number($<HTMLSelectElement>('.free-months', row).value);
        const ok = await confirmDialog({
          title: `Give ${months} free month${months > 1 ? 's' : ''}?`,
          body: html`<p><b>${row.dataset.name}</b> goes live (or stays live) with no charge for ${months} month${months > 1 ? 's' : ''}. If they’re already paying, their next charge moves back.</p>
            <p class="muted">At the end, it keeps going if they’ve added a card under Billing. If not, the tour pauses.</p>`,
          confirm: 'Give free time',
        });
        if (!ok) return;
        btn.disabled = true;
        const r = await adminGiveFree(row.dataset.project!, months);
        toast(`${row.dataset.name} is free until ${day(r.free_until)}`);
        await reload();
      } else if (act === 'cancel' && row) {
        const ok = await confirmDialog({
          title: 'Cancel this tour?',
          body: html`<p>Stops billing for <b>${row.dataset.name}</b> straight away and pauses its link. No refund is made automatically. Their renders are kept, so they can publish again.</p>`,
          confirm: 'Cancel tour', danger: true,
        });
        if (!ok) return;
        btn.disabled = true;
        await adminCancelTour(row.dataset.project!);
        toast(`${row.dataset.name} is paused`);
        await reload();
      } else if (act === 'delete') {
        const email = card.dataset.email!;
        const n = $$('.ap-row', card).length;
        const ok = await confirmDialog({
          title: 'Delete this account?',
          body: html`<p>Deletes <b>${email}</b>, any studio only they own, ${n ? raw(`its <b>${n} project${n > 1 ? 's' : ''}</b> and every render file`) : 'and their sign-in'}. Live tours stop working and their subscriptions are cancelled.</p><p><b>This can’t be undone.</b></p>`,
          confirm: 'Delete account', danger: true, typeToConfirm: email,
        });
        if (!ok) return;
        btn.disabled = true;
        const r = await adminDeleteAccount(card.dataset.user!);
        toast(`${email} deleted${r.projects ? ` with ${r.projects} project${r.projects > 1 ? 's' : ''}` : ''}`);
        await reload();
      }
    } catch (err) {
      btn.disabled = false;
      toast(err instanceof Error ? err.message : 'That didn’t work', 'error', 8000);
    }
  });
}
